import { classify } from 'sidecar-utilities'

import { ContentWidths } from './ContentSection.types'
import { Sentiment } from '@sidecar-components/types'

export interface outerSectionStylesProps {
  breathable?: boolean
  compact?: boolean
  sentiment?: Sentiment
  strong?: boolean
}
export const outerSectionStyles = ({
  breathable,
  compact,
  sentiment = Sentiment.DEFAULT,
  strong,
}: outerSectionStylesProps) =>
  classify({
    width: 'w-full',
    // when breathable, we want the section to be at least ~2/3 of the "fold".
    // shrink-0 is used to make sure min-h is a minimum vs a maximum which the
    // appContent's overflow-auto kinda makes it do
    height: breathable ? 'min-h-120 shrink-0' : null,
    // items-center is used to center the innerSection when its smaller than the outerSection's min-h
    flex: breathable ? 'flex items-center' : null,
    verticalSpace: breathable
      ? 'py-10 md:py-18 lg:py-24'
      : compact
        ? 'py-5 md:py-6 lg:py-10'
        : // default
          'py-10',
    // var(--safe-right) is for PWA.
    // It will increase the right and left padding to make room for the shape of the phone screen.
    // See more at https://webkit.org/blog/7929/designing-websites-for-iphone-x/
    horizontalSpace: `pr-[calc(var(--safe-right)+theme(space.5))]
      md:pr-[calc(var(--safe-right)+theme(space.6))]
      lg:pr-[calc(var(--safe-right)+theme(space.8))]
      pl-[calc(var(--safe-left)+theme(space.5))]
      md:pl-[calc(var(--safe-left)+theme(space.6))]
      lg:pl-[calc(var(--safe-left)+theme(space.8))]`,
    bgColor: _getOuterBgColor(sentiment, strong),
  })

export interface innerContentStylesProps {
  sentiment?: Sentiment
  strong?: boolean
  width: ContentWidths
}
export const innerContentStyles = ({
  sentiment,
  strong,
  width,
}: innerContentStylesProps) =>
  classify({
    layout: 'w-full',
    //center the innerContent when its skinny
    center: 'mx-auto',
    gutter: 'gap-4 sm:gap-6 md:gap-8 lg:gap-12',
    defaultSize: width === 'default' ? 'max-w-6xl' : null,
    fullSize: width === 'full' ? 'max-w-screen' : null,
    skinnySize: width === 'skinny' ? 'max-w-lg' : null,
    defaultChildren:
      sentiment !== 'brand' && sentiment !== 'primary' && !strong
        ? `[&_*.inherit]:color-textOnDefault
          [&_h1.inherit]:color-textOnDefaultStrong
          [&_h2.inherit]:color-textOnDefaultStrong
          [&_h3.inherit]:color-textOnDefaultStrong
          [&_h4.inherit]:color-textOnDefaultStrong
          [&_h5.inherit]:color-textOnDefaultStrong
          [&_svg.inherit]:color-icon-default
          [&_strong.inherit]:color-textOnDefaultStrong`
        : null,
    strongChildren:
      sentiment !== 'brand' && sentiment !== 'primary' && strong
        ? `[&_*.inherit]:color-textOnAccent-default
          [&_h1.inherit]:color-textOnAccent-heavy-default
          [&_h2.inherit]:color-textOnAccent-bold-default
          [&_h3.inherit]:color-textOnAccent-bold-default
          [&_h4.inherit]:color-textOnAccent-bold-default
          [&_h5.inherit]:color-textOnAccent-bold-default
          [&_svg.inherit]:color-iconOnAccent-default
          [&_svg.inherit.color-icon-weak-default]:color-iconOnAccent-weak-default
          [&_strong.inherit]:color-textAccent-bold`
        : null,
    brandChildren:
      sentiment === 'brand'
        ? `[&_*.inherit]:color-textOnBrand-default
          [&_h1.inherit]:color-textOnBrand-heavy
          [&_h2.inherit]:color-textOnBrand-bold-default
          [&_h3.inherit]:color-textOnBrand-bold-default
          [&_h4.inherit]:color-textOnBrand-bold-default
          [&_h5.inherit]:color-textOnBrand-bold-default
          [&_svg.inherit]:color-iconOnBrand-default
          [&_svg.inherit.color-icon-weak-default]:color-iconOnBrand-weak-default
          [&_strong.inherit]:color-textOnBrand-bold`
        : null,
    primaryChildren:
      sentiment === 'primary'
        ? `[&_*.inherit]:color-textOnPrimary
          [&_svg.inherit]:color-iconOnPrimary`
        : null,
  })

function _getOuterBgColor(sentiment: Sentiment, strong?: boolean) {
  if (sentiment === 'brand') return 'color-bgBrand'
  if (sentiment === 'primary') return 'color-bgPrimary'
  if (strong) return 'color-bgDefaultStrong'
  return 'color-bgDefault'
}
